import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Bell, Check, X, Megaphone, HandshakeIcon, TrendingUp, Heart, MessageCircle, Sparkles, BadgeAlert, Trophy, UserPlus, Gift } from "lucide-react";

const ICON_MAP = {
  announcement: Megaphone,
  trade_interest: HandshakeIcon,
  price_alert: TrendingUp,
  haul_like: Heart,
  haul_comment: MessageCircle,
  suggestion_approved: Sparkles,
  suggestion_rejected: BadgeAlert,
  badge_unlocked: Trophy,
  new_follower: UserPlus,
  wishlist_match: Gift,
};

function NotificationItem({ item, onRead, onNavigate }) {
  const Icon = ICON_MAP[item.type] || Bell;
  return (
    <div
      data-testid={`notification-${item.notification_id}`}
      onClick={() => { if (!item.read) onRead(item.notification_id); if (item.link) onNavigate(item.link); }}
      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${item.read ? "opacity-60 hover:opacity-80" : "bg-zinc-800/30 hover:bg-zinc-800/50"}`}
    >
      <div className={`p-1.5 rounded-full flex-shrink-0 ${item.read ? "bg-zinc-800" : "bg-rose-600/10"}`}>
        <Icon className={`w-3.5 h-3.5 ${item.read ? "text-zinc-500" : "text-rose-400"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${item.read ? "text-zinc-500" : "text-zinc-200 font-medium"}`}>{item.title}</p>
        <p className="text-[11px] text-zinc-600 mt-0.5">{item.body}</p>
        <p className="text-[10px] text-zinc-700 mt-1">{new Date(item.created_at).toLocaleDateString()}</p>
      </div>
      {!item.read && <div className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0 mt-1.5" />}
    </div>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications?limit=20");
      setNotifications(data.notifications);
      setUnread(data.unread_count);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Poll for new notifications every 30s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const markOneRead = async (notifId) => {
    try {
      await api.post(`/notifications/${notifId}/read`);
      setNotifications((prev) => prev.map((n) => n.notification_id === notifId ? { ...n, read: true } : n));
      setUnread((prev) => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded-lg transition-colors"
      >
        <Bell className="w-4.5 h-4.5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden animate-fadeIn origin-top-right md:right-auto md:left-0 md:origin-top-left">
          <div className="flex items-center justify-between p-3 border-b border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-200">Notifications</h3>
            <div className="flex gap-1">
              {unread > 0 && (
                <button data-testid="mark-all-read" onClick={markAllRead} className="text-[10px] text-rose-400 hover:text-rose-300 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-zinc-500 hover:text-zinc-300">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-600 text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="p-1 space-y-0.5">
                {notifications.map((n) => (
                  <NotificationItem key={n.notification_id} item={n} onRead={markOneRead} onNavigate={(link) => { setOpen(false); navigate(link); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
